import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/d9u4r0tca23r0tr1aqjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const isSpam = (serverMsg || '').toLowerCase().includes('spam');
        if (isSpam) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '正太漫画研究所',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '正太漫画专题网站，致力于为漫画爱好者与创作者提供优质的正太风格漫画作品推荐、绘画教程与同人创作资源。',
    sameAs: [
      'https://weibo.com/zhengtai-manga',
      'https://space.bilibili.com/zhengtai-manga',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@zhengtai-manga.com',
      contactType: 'customer service',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '正太漫画适合什么年龄段的读者？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '正太漫画作为一种漫画美学风格，适合所有对漫画艺术感兴趣的读者。内容涵盖作品推荐、绘画教程、风格解析等，不同作品有不同的受众定位，读者可根据自身兴趣选择。',
        },
      },
      {
        '@type': 'Question',
        name: '如何开始学习正太漫画的绘制？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '建议从基础的人物比例和表情刻画开始，我们的"绘画教程"分类提供了从入门到进阶的系统教程，同时可以参考"作品推荐"中的经典作品进行临摹练习。',
        },
      },
      {
        '@type': 'Question',
        name: '如何投稿自己的正太漫画作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页面的"联系我们"表单提交你的作品信息和联系方式，我们的编辑团队会在3个工作日内与你取得联系。',
        },
      },
    ],
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

            {/* Brand Introduction */}
            <section className="mb-16">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-6">
                关于正太漫画
              </h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    <strong>正太漫画研究所</strong>成立于2024年，是国内领先的专注正太漫画风格的垂直内容平台。我们致力于为正太漫画爱好者和创作者提供高质量的作品推荐、系统化的绘画教程和活跃的同人创作交流空间。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    正太漫画作为一种独特的漫画美学风格，以其清新可爱的少年形象和细腻的情感表达深受广大读者喜爱。然而，国内长期以来缺乏系统化、专业化的正太漫画内容平台。我们的使命正是填补这一空白——让更多人了解、欣赏并参与到正太漫画的创作与交流中来。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    截至目前，我们已收录超过500篇正太漫画相关文章，涵盖了作品推荐、绘画教程、同人创作和风格解析四大核心板块，累计服务了超过10万名漫画爱好者与创作者。
                  </p>
                </div>
                <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=cozy%20creative%20studio%20space%20with%20manga%20drawing%20tablet%20and%20art%20supplies%20warm%20ambient%20lighting%20bookshelf%20full%20of%20comics%20Japanese%20aesthetic%20minimalist%20decor%20peaceful%20atmosphere&width=800&height=500&seq=about-brand-01&orientation=landscape"
                    alt="正太漫画研究所 - 品牌介绍"
                    className="w-full h-full object-cover"
                    title="正太漫画研究所创意空间"
                  />
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16 py-10 bg-background-100 rounded-2xl border border-background-200/70">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 px-6">
                {[
                  { value: '500+', label: '原创文章' },
                  { value: '100,000+', label: '月活跃读者' },
                  { value: '2,000+', label: '注册创作者' },
                  { value: '98%', label: '读者好评率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-accent-600 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-600 mb-8">来自创作者和读者的真实反馈</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '小林画师',
                    role: '独立漫画创作者',
                    text: '正太漫画研究所的绘画教程非常系统，从人物比例到色彩搭配，帮我少走了很多弯路。尤其是表情刻画那篇，看完后角色的表现力提升了一大截！',
                    avatar: 'https://readdy.ai/api/search-image?query=soft%20anime%20style%20portrait%20young%20artist%20with%20glasses%20gentle%20smile%20warm%20lighting%20clean%20background%20professional%20headshot&width=120&height=120&seq=avatar-01&orientation=squarish',
                  },
                  {
                    name: '阿星',
                    role: '漫画爱好者',
                    text: '作为一个正太漫画的忠实读者，终于找到一个专业的中文平台了！作品推荐栏目帮我发现了好多宝藏漫画，每篇推荐都写得很用心。',
                    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20portrait%20young%20woman%20with%20warm%20smile%20casual%20style%20soft%20lighting%20clean%20background%20friendly%20expression&width=120&height=120&seq=avatar-02&orientation=squarish',
                  },
                  {
                    name: '画画的清风',
                    role: '同人画师',
                    text: '同人创作社区的氛围特别好，大家互相鼓励互相学习。在这里分享作品总能收到真诚的反馈，对我的进步帮助很大。',
                    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20portrait%20creative%20person%20holding%20drawing%20tablet%20pen%20warm%20expression%20soft%20lighting%20clean%20background&width=120&height=120&seq=avatar-03&orientation=squarish',
                  },
                ].map((review) => (
                  <div
                    key={review.name}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
                常见问题
              </h2>
              <div className="space-y-3 max-w-3xl">
                {[
                  {
                    q: '正太漫画适合什么年龄段的读者？',
                    a: '正太漫画作为一种漫画美学风格，适合所有对漫画艺术感兴趣的读者。内容涵盖作品推荐、绘画教程、风格解析等，不同作品有不同的受众定位，读者可根据自身兴趣选择。',
                  },
                  {
                    q: '如何开始学习正太漫画的绘制？',
                    a: '建议从基础的人物比例和表情刻画开始，我们的"绘画教程"分类提供了从入门到进阶的系统教程，同时可以参考"作品推荐"中的经典作品进行临摹练习。',
                  },
                  {
                    q: '网站内容多久更新一次？',
                    a: '我们保持每周至少3篇新内容的更新频率，涵盖教程、推荐和风格解析等多个方向，确保读者始终能获取新鲜的正太漫画资讯。',
                  },
                  {
                    q: '如何投稿自己的正太漫画作品？',
                    a: '你可以通过下方"联系我们"表单提交你的作品信息和联系方式，我们的编辑团队会在3个工作日内与你取得联系。',
                  },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section
              id="contact"
              className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/70"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mb-8">
                有任何问题、建议或合作意向，请随时与我们联系
              </p>

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleContactSubmit}
                className="max-w-2xl space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请选择或输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多可输入500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600">留言已成功发送！我们会尽快回复你。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
              </form>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}